/**
 * Local Storage Manager - localStorage cache operations
 * 
 * Handles all localStorage operations for file caching
 */

/**
 * LocalStorageManager Class - Manages localStorage cache operations
 */
class LocalStorageManager {
    constructor() {
        this.cacheKeys = {
            files: 'filesList',
            date: 'filesDate',
            metadata: 'filesMetadata',
            version: 'cacheVersion'
        };
        
        this.currentVersion = '4.0.0';
        
        this.stats = {
            hits: 0,
            misses: 0,
            invalidations: 0,
            lastUpdate: null
        };
    }
    
    /**
     * Get cached files date from localStorage
     * @returns {number|null} - Timestamp or null if invalid
     */
    getCachedDate() {
        try {
            const dateStr = localStorage.getItem(this.cacheKeys.date);
            if (!dateStr) return null;
            
            const date = Number(dateStr);
            return (!isNaN(date) && date > 0) ? date : null;
        } catch (error) {
            const debugMode = new URLSearchParams(window.location.search).get('debug');
            if (debugMode === '1') {
                console.warn('[LocalStorageManager] Error reading cached date:', error);
            }
            return null;
        }
    }
    
    /**
     * Set cached files date in localStorage
     * @param {number} timestamp - Timestamp to cache
     */
    setCachedDate(timestamp) {
        try {
            localStorage.setItem(this.cacheKeys.date, timestamp.toString());
            
            if (window.HyperLog) {
                window.HyperLog.debug('[LocalStorageManager] Date cached', { 
                    timestamp: timestamp,
                    date: new Date(timestamp).toISOString()
                });
            }
        } catch (error) {
            const debugMode = new URLSearchParams(window.location.search).get('debug');
            if (debugMode === '1') {
                console.error('[LocalStorageManager] Error setting cached date:', error);
            }
        }
    }
    
    /**
     * Get cached files list from localStorage
     * @param {Function} unzip - Unzip function for decompression
     * @returns {Array|null} - Files array or null if invalid
     */
    getCachedFiles(unzip) {
        try {
            const compressed = localStorage.getItem(this.cacheKeys.files);
            if (!compressed) {
                this.stats.misses++;
                return null;
            }
            
            const files = unzip(compressed);
            if (!Array.isArray(files)) {
                const debugMode = new URLSearchParams(window.location.search).get('debug');
                if (debugMode === '1') {
                    console.warn('[LocalStorageManager] Cached files are not an array');
                }
                this.invalidateCache();
                this.stats.misses++;
                return null;
            }
            
            this.stats.hits++;
            
            if (window.HyperLog) {
                window.HyperLog.debug('[LocalStorageManager] Cache hit', { 
                    filesCount: files.length,
                    cacheHits: this.stats.hits
                });
            }
            
            return files;
        } catch (error) {
            const debugMode = new URLSearchParams(window.location.search).get('debug');
            if (debugMode === '1') {
                console.error('[LocalStorageManager] Error reading cached files:', error);
            }
            this.invalidateCache();
            this.stats.misses++;
            return null;
        }
    }
    
    /**
     * Set cached files list in localStorage
     * @param {string} compressedData - Compressed files data
     * @returns {boolean} - Success status
     */
    setCachedFiles(compressedData) {
        try {
            localStorage.setItem(this.cacheKeys.files, compressedData);
            
            // Update metadata
            this.setCacheMetadata({
                version: this.currentVersion,
                timestamp: Date.now(),
                size: compressedData.length
            });
            
            if (window.HyperLog) {
                window.HyperLog.debug('[LocalStorageManager] Files cached', { 
                    dataLength: compressedData.length,
                    version: this.currentVersion
                });
            }
            
            return true;
        } catch (error) {
            const debugMode = new URLSearchParams(window.location.search).get('debug');
            if (debugMode === '1') {
                console.error('[LocalStorageManager] Error caching files:', error);
            }
            return false;
        }
    }
    
    /**
     * Get cache metadata
     * @returns {Object|null} - Metadata object or null
     */
    getCacheMetadata() {
        try {
            const metadataStr = localStorage.getItem(this.cacheKeys.metadata);
            return metadataStr ? JSON.parse(metadataStr) : null;
        } catch (error) {
            const debugMode = new URLSearchParams(window.location.search).get('debug');
            if (debugMode === '1') {
                console.warn('[LocalStorageManager] Error reading cache metadata:', error);
            }
            return null;
        }
    }
    
    /**
     * Set cache metadata
     * @param {Object} metadata - Metadata to store
     */
    setCacheMetadata(metadata) {
        try {
            const metadataStr = JSON.stringify({
                ...metadata,
                updatedAt: new Date().toISOString()
            });
            localStorage.setItem(this.cacheKeys.metadata, metadataStr);
        } catch (error) {
            const debugMode = new URLSearchParams(window.location.search).get('debug');
            if (debugMode === '1') {
                console.error('[LocalStorageManager] Error setting cache metadata:', error);
            }
        }
    }
    
    /**
     * Invalidate entire cache
     */
    invalidateCache() {
        try {
            Object.values(this.cacheKeys).forEach(key => {
                localStorage.removeItem(key);
            });
            
            this.stats.invalidations++;
            
            if (window.HyperLog) {
                window.HyperLog.debug('[LocalStorageManager] Cache invalidated', { 
                    invalidations: this.stats.invalidations
                });
            }
        } catch (error) {
            const debugMode = new URLSearchParams(window.location.search).get('debug');
            if (debugMode === '1') {
                console.error('[LocalStorageManager] Error invalidating cache:', error);
            }
        }
    }
    
    /**
     * Clear all localStorage (nuclear option)
     */
    clearAllStorage() {
        try {
            const keysRemoved = Object.keys(localStorage).length;
            localStorage.clear();
            
            if (window.HyperLog) {
                window.HyperLog.debug('[LocalStorageManager] All localStorage cleared', { 
                    keysRemoved: keysRemoved
                });
            }
        } catch (error) {
            const debugMode = new URLSearchParams(window.location.search).get('debug');
            if (debugMode === '1') {
                console.error('[LocalStorageManager] Error clearing localStorage:', error);
            }
        }
    }
    
    /**
     * Get cache statistics
     * @returns {Object} - Statistics object
     */
    getStats() {
        return {
            ...this.stats,
            cacheSize: this.getCacheSize(),
            version: this.currentVersion
        };
    }
    
    /**
     * Get approximate cache size in bytes
     * @returns {number} - Cache size
     */
    getCacheSize() {
        try {
            let totalSize = 0;
            Object.values(this.cacheKeys).forEach(key => {
                const item = localStorage.getItem(key);
                if (item) {
                    totalSize += item.length;
                }
            });
            return totalSize;
        } catch (error) {
            return 0;
        }
    }
    
    /**
     * Reset statistics
     */
    resetStats() {
        this.stats = {
            hits: 0,
            misses: 0,
            invalidations: 0,
            lastUpdate: null
        };
        
        if (window.HyperLog) {
            window.HyperLog.debug('[LocalStorageManager] Statistics reset');
        }
    }
}

export { LocalStorageManager };

